import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Unlock } from 'lucide-react';
import { useCanvasStore } from './canvas-store';
import { useToast } from '@/hooks/use-toast';

type BrandIntelRow = { useForCreation?: boolean | null; identityAnalyzedAt?: string | null };

/**
 * Compact "Brand Identity" on/off pill for the prompt input action row — a
 * second access point to the same persistent per-brand BIL switch that lives
 * in Heartbeat → Brand Identity (brand_intelligence.useForCreation). When ON,
 * every ad generated for the active brand locks to its colors/fonts/vibe.
 *
 * Renders only when the active brand has a brand_intelligence row (GET 200);
 * a 404 (no intelligence) or 403 (non-admin) returns null from the queryFn so
 * the pill is simply hidden. Styled to match the adjacent ratio selector.
 */
export function BrandIdentityToggle() {
  const activeBrandId = useCanvasStore((s) => s.activeBrandId);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ['brand-identity-toggle', activeBrandId] as const;

  const { data } = useQuery<BrandIntelRow | null>({
    queryKey,
    enabled: !!activeBrandId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fetch(`/api/admin/brand-intelligence/${activeBrandId}`);
      if (!res.ok) return null;
      return res.json();
    },
  });

  const toggle = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await fetch(`/api/admin/brand-intelligence/${activeBrandId}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ useForCreation: next }),
      });
      if (!res.ok) throw new Error('toggle failed');
      return next;
    },
    onMutate: async (next: boolean) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BrandIntelRow | null>(queryKey);
      queryClient.setQueryData<BrandIntelRow | null>(queryKey, (r) => (r ? { ...r, useForCreation: next } : r));
      return { prev };
    },
    onError: (_e, _next, ctx) => {
      queryClient.setQueryData(queryKey, ctx?.prev ?? null);
      toast({ title: 'Couldn’t update Brand Identity', variant: 'destructive' });
    },
    onSuccess: (next: boolean) => {
      toast({ title: next ? 'Brand Identity locked on' : 'Brand Identity off' });
    },
  });

  if (!activeBrandId || !data) return null;
  const on = !!data.useForCreation;

  return (
    <button
      type="button"
      data-testid="brand-identity-toggle"
      onClick={(e) => { e.stopPropagation(); if (!toggle.isPending) toggle.mutate(!on); }}
      title={on
        ? 'Brand Identity is ON — ads lock to this brand’s colors, fonts, and vibe'
        : 'Brand Identity is OFF — baseline ad generation'}
      className="flex items-center gap-1 transition-colors"
      style={{
        height: 28,
        padding: '0 8px',
        borderRadius: 8,
        background: on ? '#1A1A1A' : '#F5F3EE',
        border: on ? '1px solid #1A1A1A' : '1px solid #E5E2DC',
        fontSize: 11,
        fontWeight: 600,
        color: on ? '#FFFFFF' : '#3D3A36',
        cursor: 'pointer',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        opacity: toggle.isPending ? 0.6 : 1,
      }}
    >
      {on
        ? <Lock style={{ width: 11, height: 11 }} />
        : <Unlock style={{ width: 11, height: 11, color: '#8C8578' }} />}
      Brand Identity
    </button>
  );
}
